import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FaHome, FaHandsHelping, FaComments, FaCalendarDay, FaFileSignature } from 'react-icons/fa';
import SummaryStatCard from './SummaryStatCard';
import DashboardStatGroup from './DashboardStatGroup';
import AttendanceTodayCard from './AttendanceTodayCard';
import ExamMarkingPipelineCard from './ExamMarkingPipelineCard';
import Loader from '../../components/Loader/Loader';
import { getAdminDashboardSummary } from '../../services/DashboardService';
import './Dashboard.css';

const AdminDashboard = () => {
  const [summary, setSummary] = useState(null);

  const permissions = useMemo(
    () => JSON.parse(localStorage.getItem('permissions')) || [],
    [],
  );
  const can = useCallback((permission) => permissions.includes(permission), [permissions]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await getAdminDashboardSummary();
      if (response.status === 200) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (!summary) return <Loader />;

  const {
    counts,
    attendance_today,
    leave_requests,
    exam_marking_pipeline,
    grievance_queries,
    stakeholder_feedback,
    appointments,
  } = summary;

  const canViewStudents = can('student.list.view');
  const canViewTeachers = can('teacher.subject.mapping.view');
  const canViewUsers = can('users.view');
  const canViewPrograms = can('class.view');
  const canViewAttendance = can('attendance.report.view');
  const canViewMarking = can('assessment.marking.view');
  const canViewLeave = can('leave.view');
  const canViewQueries = can('query.view');
  const canViewFeedback = can('stakeholder.query.view');
  const canViewAppointments = can('appointment.view');

  const showSnapshotSection = canViewAttendance || canViewMarking;
  const showAttentionSection = canViewLeave || canViewQueries || canViewFeedback || canViewAppointments;

  return (
    <div className="dashboard-page">
      <div className="dash-page-header">
        <div className="dash-page-title d-flex align-items-center gap-2">
          <FaHome />
          <span>Dashboard</span>
        </div>
        <div className="dash-page-subtitle">Real-time overview of your school</div>
      </div>

      <div className="row">
        {canViewStudents && (
          <SummaryStatCard
            title="Students"
            variant="violet"
            icon="fa-solid fa-user-graduate"
            count={counts.students}
            link="/student/list"
          />
        )}
        {canViewTeachers && (
          <SummaryStatCard
            title="Teachers"
            variant="indigo"
            icon="fa-solid fa-user-tie"
            count={counts.teachers}
            link="/teacher/list"
          />
        )}
        {canViewUsers && (
          <SummaryStatCard
            title="Staff Users"
            variant="teal"
            icon="fa-solid fa-users"
            count={counts.staff_users}
            link="/user/list"
          />
        )}
        {canViewPrograms && (
          <SummaryStatCard
            title="Programs"
            variant="amber"
            icon="fa-solid fa-graduation-cap"
            count={counts.programs}
            link="/program/list"
          />
        )}
      </div>

      {showSnapshotSection && (
        <>
          <div className="dash-section-title">Today's Snapshot</div>
          <div className="row">
            {canViewAttendance && (
              <AttendanceTodayCard attendance={attendance_today} actionLink="/attendance/report" actionLabel="View Report" />
            )}
            {canViewMarking && (
              <ExamMarkingPipelineCard pipeline={exam_marking_pipeline} link="/assessment/marking/list" />
            )}
          </div>
        </>
      )}

      {showAttentionSection && (
        <>
          <div className="dash-section-title">Needs Your Attention</div>
          <div className="row">
            {canViewLeave && (
              <DashboardStatGroup
                title="Leave Requests"
                icon={<FaFileSignature />}
                link="/leave/list"
                metrics={[
                  { label: 'Pending', value: leave_requests.pending, color: 'warning' },
                  { label: 'Approved', value: leave_requests.approved, color: 'success' },
                  { label: 'Rejected', value: leave_requests.rejected, color: 'danger' },
                ]}
              />
            )}
            {canViewQueries && (
              <DashboardStatGroup
                title="Grievance Queries"
                icon={<FaHandsHelping />}
                link="/queries"
                metrics={[
                  { label: 'Total', value: grievance_queries.total },
                  { label: 'Open', value: grievance_queries.open, color: 'danger' },
                  { label: 'In Progress', value: grievance_queries.in_progress, color: 'warning' },
                  { label: 'Closed', value: grievance_queries.closed, color: 'success' },
                ]}
              />
            )}
            {canViewFeedback && (
              <DashboardStatGroup
                title="Stakeholder Feedback"
                icon={<FaComments />}
                link="/stakeholder-query/list"
                metrics={[
                  { label: 'Total', value: stakeholder_feedback.total },
                  { label: 'Unread', value: stakeholder_feedback.unread, color: 'danger' },
                  { label: 'Not Replied', value: stakeholder_feedback.not_replied, color: 'warning' },
                ]}
              />
            )}
            {canViewAppointments && (
              <DashboardStatGroup
                title="Appointments"
                icon={<FaCalendarDay />}
                link="/appointment/list"
                metrics={[
                  { label: 'Pending', value: appointments.pending, color: 'warning' },
                  { label: 'Today', value: appointments.today },
                ]}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
